/**
 * QMS Account Select Component
 * Searchable combobox for selecting an account to link to a QMS ticket
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Building2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useAccounts } from '@/features/installations/hooks/useAccountData';
import { getAccount } from '@/features/accounts/api';

interface QmsAccountSelectProps {
  value?: string; // account_id UUID
  onChange: (accountId: string | undefined) => void;
  disabled?: boolean;
  className?: string;
}

export function QmsAccountSelect({
  value,
  onChange,
  disabled = false,
  className,
}: QmsAccountSelectProps) {
  const { t } = useTranslation('qms');
  const [open, setOpen] = useState(false);

  // Search state for server-side filtering
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch accounts with server-side search to handle large account lists
  // Backend sorts alphabetically, so accounts like "UdeM" (starting with U) need search
  const { data: accountsData, isLoading } = useAccounts({
    search: searchQuery || undefined,
    types: 'customer,both',
    pageLength: 100,
  });

  // Fetch the selected account separately if it's not in the list
  // This handles the case where the account is not in the first 100 alphabetically
  const { data: fetchedSelectedAccount } = useQuery({
    queryKey: ['account', value],
    queryFn: () => getAccount(value!),
    enabled: !!value && !accountsData?.accounts.some((a) => a.id === value),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Find selected account - first check the list, then use the separately fetched one
  const selectedAccount = accountsData?.accounts.find((a) => a.id === value) || fetchedSelectedAccount;

  const handleSelect = (accountId: string) => {
    onChange(accountId);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
  };

  return (
    <div className={cn('relative', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-auto min-h-10"
            disabled={disabled || isLoading}
          >
            {isLoading ? (
              <span className="text-muted-foreground">Loading...</span>
            ) : selectedAccount ? (
              <div className="flex flex-col items-start flex-1 overflow-hidden">
                <span className="truncate">{selectedAccount.name}</span>
                <span className="text-xs text-muted-foreground truncate">
                  {selectedAccount.account_number}
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground">
                {t('form.placeholders.selectAccount')}
              </span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {value && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClear(e as unknown as React.MouseEvent);
                    }
                  }}
                  className="p-1 hover:bg-gray-100 rounded cursor-pointer"
                  aria-label="Clear account"
                >
                  <X className="h-3 w-3 text-gray-400" />
                </span>
              )}
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t('form.placeholders.searchAccount')}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              <CommandEmpty>{t('form.placeholders.noAccountFound')}</CommandEmpty>
              <CommandGroup>
                {accountsData?.accounts.map((account) => (
                  <CommandItem
                    key={account.id}
                    value={`${account.name} ${account.account_number}`}
                    onSelect={() => handleSelect(account.id)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === account.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />
                    <div className="flex flex-col overflow-hidden">
                      <span className="truncate">{account.name}</span>
                      <span className="text-xs text-muted-foreground truncate">
                        {account.account_number}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
